import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { TermsModal } from './TermsModal';
import { PrivacyModal } from './PrivacyModal';

export interface SocialConsentAgreements {
  agreeTerms: boolean;
  agreePrivacy: boolean;
  agreeMarketing: boolean;
}

interface SocialConsentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  provider: 'google' | 'naver' | 'kakao';
  onConfirm: (agreements: SocialConsentAgreements) => void;
}

const PROVIDER_LABELS: Record<SocialConsentModalProps['provider'], { name: string; color: string; icon: React.ReactNode }> = {
  google: {
    name: 'Google',
    color: 'text-[#4285F4]',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
    ),
  },
  naver: {
    name: '네이버',
    color: 'text-[#2DB400]',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#2DB400">
        <path d="M16.273 12.845L7.727 0h-7.727v24h6.545v-11.155l8.546 11.155h7.727v-24h-6.545v12.845z"/>
      </svg>
    ),
  },
  kakao: {
    name: '카카오',
    color: 'text-[#3A1D1D]',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#3A1D1D">
        <path d="M12 3C6.477 3 2 6.477 2 10.737c0 2.738 1.748 5.139 4.38 6.555-.193.718-1.261 4.694-1.298 4.951 0 0-.018.157.082.218.099.061.214.013.214.013.28-.039 3.233-2.121 3.786-2.514A11.82 11.82 0 0012 20.474c5.523 0 10-3.477 10-7.737S17.523 3 12 3z"/>
      </svg>
    ),
  },
};

export function SocialConsentModal({ open, onOpenChange, provider, onConfirm }: SocialConsentModalProps) {
  const [agreeAll, setAgreeAll] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeMarketing, setAgreeMarketing] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const providerInfo = PROVIDER_LABELS[provider];

  const handleAgreeAll = (checked: boolean) => {
    setAgreeAll(checked);
    setAgreeTerms(checked);
    setAgreePrivacy(checked);
    setAgreeMarketing(checked);
  };

  const handleIndividualChange = (field: 'terms' | 'privacy' | 'marketing', checked: boolean) => {
    const next = { agreeTerms, agreePrivacy, agreeMarketing, [field === 'terms' ? 'agreeTerms' : field === 'privacy' ? 'agreePrivacy' : 'agreeMarketing']: checked };
    setAgreeTerms(next.agreeTerms);
    setAgreePrivacy(next.agreePrivacy);
    setAgreeMarketing(next.agreeMarketing);
    setAgreeAll(next.agreeTerms && next.agreePrivacy && next.agreeMarketing);
  };

  const handleConfirm = () => {
    onConfirm({ agreeTerms, agreePrivacy, agreeMarketing });
    // 상태 초기화
    setAgreeAll(false);
    setAgreeTerms(false);
    setAgreePrivacy(false);
    setAgreeMarketing(false);
  };

  const handleClose = () => {
    setAgreeAll(false);
    setAgreeTerms(false);
    setAgreePrivacy(false);
    setAgreeMarketing(false);
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-1">
              {providerInfo.icon}
              <DialogTitle className="text-xl">
                {providerInfo.name}로 가입하기
              </DialogTitle>
            </div>
            <DialogDescription>
              서비스 이용을 위해 아래 약관에 동의해 주세요.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            {/* 전체 동의 */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <input
                type="checkbox"
                id="social-agree-all"
                checked={agreeAll}
                onChange={(e) => handleAgreeAll(e.target.checked)}
                className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer"
              />
              <label htmlFor="social-agree-all" className="text-sm font-semibold text-gray-800 cursor-pointer">
                전체 동의하기
              </label>
            </div>

            <div className="space-y-3 pl-1">
              {/* 이용약관 */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="social-agree-terms"
                  checked={agreeTerms}
                  onChange={(e) => handleIndividualChange('terms', e.target.checked)}
                  className="w-4 h-4 mt-0.5 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer"
                />
                <span className="text-sm text-gray-700">
                  <button
                    type="button"
                    onClick={() => setShowTermsModal(true)}
                    className="text-primary hover:underline font-medium"
                  >
                    이용약관
                  </button>
                  에 동의합니다&nbsp;<span className="text-red-500">*</span>
                </span>
              </div>

              {/* 개인정보 처리방침 */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="social-agree-privacy"
                  checked={agreePrivacy}
                  onChange={(e) => handleIndividualChange('privacy', e.target.checked)}
                  className="w-4 h-4 mt-0.5 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer"
                />
                <span className="text-sm text-gray-700">
                  <button
                    type="button"
                    onClick={() => setShowPrivacyModal(true)}
                    className="text-primary hover:underline font-medium"
                  >
                    개인정보 처리방침
                  </button>
                  에 동의합니다&nbsp;<span className="text-red-500">*</span>
                </span>
              </div>

              {/* 마케팅 */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="social-agree-marketing"
                  checked={agreeMarketing}
                  onChange={(e) => handleIndividualChange('marketing', e.target.checked)}
                  className="w-4 h-4 mt-0.5 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer"
                />
                <label htmlFor="social-agree-marketing" className="text-sm text-gray-700 cursor-pointer">
                  마케팅 정보 수신에 동의합니다&nbsp;
                  <span className="text-gray-400">(선택)</span>
                </label>
              </div>
            </div>

            <p className="text-xs text-gray-500 pt-1">
              * 필수 항목에 동의하지 않으면 서비스를 이용할 수 없습니다.
            </p>

            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={handleClose}
              >
                취소
              </Button>
              <Button
                type="button"
                className="flex-1"
                disabled={!agreeTerms || !agreePrivacy}
                onClick={handleConfirm}
              >
                동의하고 계속하기
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {showTermsModal && <TermsModal open={showTermsModal} onOpenChange={setShowTermsModal} />}
      {showPrivacyModal && <PrivacyModal open={showPrivacyModal} onOpenChange={setShowPrivacyModal} />}
    </>
  );
}
